import { clonePattern, createPattern, ensureColor, touch } from '../domain/pattern';
import { EMPTY } from '../domain/types';
import type { BeadColor, Pattern, Rect } from '../domain/types';
import type { Op } from './draw';

export type ToolId =
  | 'pencil'
  | 'eraser'
  | 'line'
  | 'rect'
  | 'ellipse'
  | 'fill'
  | 'replace'
  | 'picker'
  | 'select'
  | 'pan';

export type ViewMode = 'chart' | 'realistic';

export interface ViewState {
  /** Pixels per millimetre. */
  zoom: number;
  panX: number;
  panY: number;
  mode: ViewMode;
  showGrid: boolean;
  showGuides: boolean;
  showSymbols: boolean;
  showRulers: boolean;
  showThread: boolean;
  showRope: boolean;
}

export interface ReferenceImage {
  src: string;
  opacity: number;
  visible: boolean;
  /** Placement in grid units, so it stays aligned when you zoom. */
  x: number;
  y: number;
  scale: number;
}

export interface Floating {
  cells: Uint16Array;
  w: number;
  h: number;
  x: number;
  y: number;
}

export interface ClipboardData {
  cells: Uint16Array;
  w: number;
  h: number;
  palette: BeadColor[];
}

export interface EditorState {
  pattern: Pattern;
  /** Bumped whenever pattern cells change in place, to drive re-renders. */
  rev: number;
  tool: ToolId;
  primary: number;
  secondary: number;
  brushSize: number;
  shapeFilled: boolean;
  symH: boolean;
  symV: boolean;
  view: ViewState;
  selection: Rect | null;
  floating: Floating | null;
  clipboard: ClipboardData | null;
  reference: ReferenceImage | null;
  /** Cells shown but not yet committed (shape tool drag). */
  preview: Op[];
  libraryId: string;
  status: string;
  canUndo: boolean;
  canRedo: boolean;
  dirty: boolean;
}

const HISTORY_LIMIT = 60;

function initialView(): ViewState {
  return {
    zoom: 9,
    panX: 0,
    panY: 0,
    mode: 'chart',
    showGrid: true,
    showGuides: true,
    showSymbols: false,
    showRulers: true,
    showThread: false,
    showRope: false,
  };
}

export class EditorStore {
  private state: EditorState;
  private listeners = new Set<() => void>();
  private undoStack: Pattern[] = [];
  private redoStack: Pattern[] = [];
  private strokeOpen = false;
  private cached: EditorState;

  constructor(pattern?: Pattern) {
    this.state = {
      pattern: pattern ?? createPattern(),
      rev: 0,
      tool: 'pencil',
      primary: EMPTY,
      secondary: EMPTY,
      brushSize: 1,
      shapeFilled: false,
      symH: false,
      symV: false,
      view: initialView(),
      selection: null,
      floating: null,
      clipboard: null,
      reference: null,
      preview: [],
      libraryId: 'delica11',
      status: '',
      canUndo: false,
      canRedo: false,
      dirty: false,
    };
    this.cached = { ...this.state };
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): EditorState => this.cached;

  private notify(): void {
    this.cached = { ...this.state };
    for (const fn of this.listeners) fn();
  }

  set(partial: Partial<EditorState>): void {
    Object.assign(this.state, partial);
    this.notify();
  }

  setView(partial: Partial<ViewState>): void {
    this.state.view = { ...this.state.view, ...partial };
    this.notify();
  }

  get pattern(): Pattern {
    return this.state.pattern;
  }

  status(msg: string): void {
    this.set({ status: msg });
  }

  // ---- history -----------------------------------------------------------

  private pushUndo(): void {
    this.undoStack.push(clonePattern(this.state.pattern));
    if (this.undoStack.length > HISTORY_LIMIT) this.undoStack.shift();
    this.redoStack = [];
  }

  /** Replace the pattern wholesale, recording one undo step. */
  commit(fn: (p: Pattern) => Pattern, status?: string): void {
    this.pushUndo();
    const next = fn(this.state.pattern);
    this.state.pattern = touch(next);
    this.state.rev += 1;
    this.state.dirty = true;
    if (status) this.state.status = status;
    this.syncHistoryFlags();
    this.notify();
  }

  /**
   * Open a stroke: one undo entry covers everything until `endStroke`, so a
   * whole drag of the pencil undoes in a single step.
   */
  beginStroke(): void {
    if (this.strokeOpen) return;
    this.pushUndo();
    this.strokeOpen = true;
    this.syncHistoryFlags();
  }

  endStroke(): void {
    this.strokeOpen = false;
    this.state.preview = [];
    this.notify();
  }

  /** Discard the in-progress stroke and restore what was there before it. */
  cancelStroke(): void {
    if (!this.strokeOpen) return;
    const prev = this.undoStack.pop();
    this.strokeOpen = false;
    if (prev) {
      this.state.pattern = prev;
      this.state.rev += 1;
    }
    this.state.preview = [];
    this.syncHistoryFlags();
    this.notify();
  }

  /** Paint cells in place. Must be inside a stroke to be undoable. */
  applyOps(ops: Op[]): void {
    if (ops.length === 0) return;
    const p = this.state.pattern;
    let changed = false;
    for (const op of ops) {
      const i = op.row * p.width + op.col;
      if (i < 0 || i >= p.cells.length) continue;
      if (p.cells[i] === op.value) continue;
      p.cells[i] = op.value;
      changed = true;
    }
    if (!changed) return;
    p.meta.modified = Date.now();
    this.state.rev += 1;
    this.state.dirty = true;
    this.notify();
  }

  setPreview(ops: Op[]): void {
    this.state.preview = ops;
    this.state.rev += 1;
    this.notify();
  }

  undo(): void {
    const prev = this.undoStack.pop();
    if (!prev) return;
    this.redoStack.push(clonePattern(this.state.pattern));
    this.state.pattern = prev;
    this.state.rev += 1;
    this.state.floating = null;
    this.state.dirty = true;
    this.syncHistoryFlags();
    this.state.status = 'Undo';
    this.notify();
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push(clonePattern(this.state.pattern));
    this.state.pattern = next;
    this.state.rev += 1;
    this.state.floating = null;
    this.state.dirty = true;
    this.syncHistoryFlags();
    this.state.status = 'Redo';
    this.notify();
  }

  private syncHistoryFlags(): void {
    this.state.canUndo = this.undoStack.length > 0;
    this.state.canRedo = this.redoStack.length > 0;
  }

  /** Load a different pattern; history does not carry across documents. */
  loadPattern(p: Pattern, opts: { keepView?: boolean } = {}): void {
    this.undoStack = [];
    this.redoStack = [];
    this.strokeOpen = false;
    this.state.pattern = p;
    this.state.rev += 1;
    this.state.selection = null;
    this.state.floating = null;
    this.state.preview = [];
    this.state.primary = p.palette.length > 1 ? 1 : EMPTY;
    this.state.dirty = false;
    if (!opts.keepView) this.state.view = { ...initialView(), mode: this.state.view.mode };
    this.syncHistoryFlags();
    this.notify();
  }

  markSaved(): void {
    this.set({ dirty: false });
  }

  // ---- palette -----------------------------------------------------------

  addColor(color: BeadColor, selectIt = true): number {
    let index = this.state.pattern.palette.findIndex(
      (c) => c.id === color.id || (c.hex.toLowerCase() === color.hex.toLowerCase() && c.code === color.code),
    );
    if (index < 0) {
      this.commit((p) => {
        const next = clonePattern(p);
        ensureColor(next, color);
        return next;
      }, `Added ${color.name}`);
      index = this.state.pattern.palette.length - 1;
    }
    if (selectIt) this.set({ primary: index });
    return index;
  }

  // ---- selection ---------------------------------------------------------

  setSelection(rect: Rect | null): void {
    this.set({ selection: rect });
  }

  /** Lift the selection into a floating buffer so it can be dragged. */
  liftSelection(): void {
    const { selection, pattern } = this.state;
    if (!selection || this.state.floating) return;
    const { x, y, w, h } = selection;
    const cells = new Uint16Array(w * h);
    for (let r = 0; r < h; r++) {
      for (let c = 0; c < w; c++) {
        cells[r * w + c] = pattern.cells[(y + r) * pattern.width + (x + c)];
      }
    }
    this.beginStroke();
    for (let r = 0; r < h; r++) {
      for (let c = 0; c < w; c++) {
        pattern.cells[(y + r) * pattern.width + (x + c)] = EMPTY;
      }
    }
    this.state.floating = { cells, w, h, x, y };
    this.state.rev += 1;
    this.notify();
  }

  moveFloating(x: number, y: number): void {
    if (!this.state.floating) return;
    this.state.floating = { ...this.state.floating, x, y };
    this.state.selection = { x, y, w: this.state.floating.w, h: this.state.floating.h };
    this.state.rev += 1;
    this.notify();
  }

  /** Drop the floating buffer back into the pattern. */
  stampFloating(): void {
    const f = this.state.floating;
    if (!f) return;
    const p = this.state.pattern;
    for (let r = 0; r < f.h; r++) {
      for (let c = 0; c < f.w; c++) {
        const v = f.cells[r * f.w + c];
        if (v === EMPTY) continue;
        const tr = f.y + r;
        const tc = f.x + c;
        if (tr < 0 || tr >= p.height || tc < 0 || tc >= p.width) continue;
        p.cells[tr * p.width + tc] = v;
      }
    }
    this.state.floating = null;
    this.state.rev += 1;
    this.state.dirty = true;
    this.endStroke();
  }

  copySelection(cut = false): void {
    const { selection, pattern } = this.state;
    if (!selection) return;
    const { x, y, w, h } = selection;
    const cells = new Uint16Array(w * h);
    for (let r = 0; r < h; r++) {
      for (let c = 0; c < w; c++) {
        cells[r * w + c] = pattern.cells[(y + r) * pattern.width + (x + c)];
      }
    }
    this.state.clipboard = { cells, w, h, palette: pattern.palette.map((p) => ({ ...p })) };
    if (cut) {
      this.commit((p) => {
        const next = clonePattern(p);
        for (let r = 0; r < h; r++) {
          for (let c = 0; c < w; c++) next.cells[(y + r) * next.width + (x + c)] = EMPTY;
        }
        return next;
      }, `Cut ${w}×${h}`);
    } else {
      this.status(`Copied ${w}×${h} beads`);
    }
  }

  /** Paste the clipboard as a floating selection at the given cell. */
  paste(at?: { col: number; row: number }): void {
    const clip = this.state.clipboard;
    if (!clip) return;
    const p = this.state.pattern;
    // Colours come along with the clipboard so pasting between patterns works.
    const remap = new Map<number, number>();
    this.commit((pat) => {
      const next = clonePattern(pat);
      clip.palette.forEach((color, i) => {
        if (i === EMPTY) return;
        remap.set(i, ensureColor(next, color));
      });
      return next;
    }, 'Paste');
    const cells = new Uint16Array(clip.cells.length);
    for (let i = 0; i < clip.cells.length; i++) {
      const v = clip.cells[i];
      cells[i] = v === EMPTY ? EMPTY : remap.get(v) ?? EMPTY;
    }
    const x = at?.col ?? Math.max(0, Math.floor((p.width - clip.w) / 2));
    const y = at?.row ?? Math.max(0, Math.floor((p.height - clip.h) / 2));
    this.beginStroke();
    this.state.floating = { cells, w: clip.w, h: clip.h, x, y };
    this.state.selection = { x, y, w: clip.w, h: clip.h };
    this.state.tool = 'select';
    this.state.rev += 1;
    this.state.status = 'Pasted — drag to place, Enter to drop';
    this.notify();
  }

  deleteSelection(): void {
    const sel = this.state.selection;
    if (!sel) return;
    if (this.state.floating) {
      this.state.floating = null;
      this.endStroke();
      return;
    }
    this.commit((p) => {
      const next = clonePattern(p);
      for (let r = 0; r < sel.h; r++) {
        for (let c = 0; c < sel.w; c++) {
          const tr = sel.y + r;
          const tc = sel.x + c;
          if (tr < 0 || tr >= next.height || tc < 0 || tc >= next.width) continue;
          next.cells[tr * next.width + tc] = EMPTY;
        }
      }
      return next;
    }, 'Cleared selection');
  }

  /** Apply a transform to just the selected rectangle. */
  transformSelection(kind: 'flipH' | 'flipV' | 'rotate180'): void {
    const sel = this.state.selection;
    if (!sel) return;
    this.commit((p) => {
      const next = clonePattern(p);
      const { x, y, w, h } = sel;
      const buf = new Uint16Array(w * h);
      for (let r = 0; r < h; r++) {
        for (let c = 0; c < w; c++) buf[r * w + c] = p.cells[(y + r) * p.width + (x + c)];
      }
      for (let r = 0; r < h; r++) {
        for (let c = 0; c < w; c++) {
          const sr = kind === 'flipH' ? r : h - 1 - r;
          const sc = kind === 'flipV' ? c : w - 1 - c;
          next.cells[(y + r) * next.width + (x + c)] = buf[sr * w + sc];
        }
      }
      return next;
    }, `Selection ${kind}`);
  }
}
